import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, BookOpen } from 'lucide-react';

/**
 * Mission Control Home footer — brand line + "Powered by Mission AI"
 * + a Data Confidence pill, matching the restyled Home mockup.
 * `dataConfidence` is a soft signal derived from whether the page has
 * live navigation-condition data (passed by the parent); defaults to
 * "High" so the footer always renders something sensible.
 */
export default function HomeFooter({ dataConfidence = 'High', lastUpdated = null }) {
  const tone = dataConfidence === 'High'
    ? 'bg-emerald-500'
    : dataConfidence === 'Medium'
      ? 'bg-amber-500'
      : 'bg-slate-400';
  return (
    <footer
      className="mt-4 flex items-center justify-between gap-4 flex-wrap border-t border-slate-200 pt-5 text-xs text-slate-400"
      data-testid="home-footer"
    >
      <div className="flex items-center gap-4 flex-wrap">
        <span
          className="inline-flex items-center gap-1.5 font-semibold text-slate-500"
          data-testid="home-footer-whats-new"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500" />
          </span>
          What&apos;s new
        </span>
        <span className="hidden sm:inline text-slate-300">·</span>
        <span className="font-semibold uppercase tracking-widest text-slate-400">
          Seertech · Mission Critical Learning
        </span>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <Link
          to="/lei-reference"
          className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-700 hover:underline"
          data-testid="home-footer-science-link"
        >
          <BookOpen className="h-3.5 w-3.5 text-indigo-500" /> The Science Behind It
        </Link>
        <span className="inline-flex items-center gap-1.5 text-slate-500" data-testid="home-footer-mission-ai">
          <Sparkles className="h-3.5 w-3.5 text-cyan-600" /> Powered by Mission AI
        </span>
        {lastUpdated && (
          <span className="text-slate-400">Last updated: {new Date(lastUpdated).toLocaleString()}</span>
        )}
        <span
          className="inline-flex items-center gap-1.5 rounded-full border border-[#22304d] bg-[#111827] px-2.5 py-1 font-semibold text-slate-300"
          data-testid="home-footer-data-confidence"
        >
          <span className={`h-2 w-2 rounded-full ${tone}`} />
          Data Confidence: {dataConfidence}
        </span>
      </div>
    </footer>
  );
}
